import React from 'react';
import { BOOKWORM_AVATARS, DEFAULT_AVATAR_ID } from './BookwormAvatars';

/**
 * UserAvatar — universal avatar renderer used everywhere in the app
 * (reviews, chat, book discussions, settings preview).
 *
 * Resolution order:
 *   1. avatarPhotoUrl     -> render uploaded photo (data URL or remote URL)
 *   2. avatarId           -> render the matching bookworm preset SVG
 *   3. fallbackPersonaName -> deterministic preset (so AI personas / legacy
 *                             users always have a stable bookworm avatar)
 *   4. initials chip
 *
 * All sizes are square. Uses CSS pixel sizes (sm=24, md=32, lg=44, xl=64).
 */

const SIZE_MAP = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 44,
  xl: 64,
  '2xl': 96,
};

// Same deterministic mapping used on the backend (avatar_for_persona)
function hashPickAvatar(name) {
  if (!name) return DEFAULT_AVATAR_ID;
  let hash = 5381;
  for (let i = 0; i < name.length; i += 1) {
    hash = ((hash << 5) + hash + name.charCodeAt(i)) | 0;
  }
  const ids = Object.keys(BOOKWORM_AVATARS);
  return ids[Math.abs(hash) % ids.length];
}

const UserAvatar = ({
  name,
  avatarId,
  avatarPhotoUrl,
  size = 'md',
  className = '',
  ringColor = '',
  testId,
}) => {
  const px = typeof size === 'number' ? size : (SIZE_MAP[size] || 36);

  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0))
    .join('')
    .toUpperCase();

  const wrapperBase = 'inline-flex items-center justify-center overflow-hidden rounded-full bg-zinc-800 select-none shrink-0 aspect-square';
  const ring = ringColor ? `ring-2 ring-offset-1 ring-offset-black ${ringColor}` : '';

  let content = null;
  let testIdSuffix = 'initials';

  if (avatarPhotoUrl) {
    testIdSuffix = 'photo';
    content = (
      <img
        src={avatarPhotoUrl}
        alt={`${name || 'User'} avatar`}
        loading="lazy"
        style={{ width: px, height: px }}
        className="block object-cover"
      />
    );
  } else {
    const presetId = avatarId && BOOKWORM_AVATARS[avatarId]
      ? avatarId
      : hashPickAvatar(name);
    const preset = BOOKWORM_AVATARS[presetId] || BOOKWORM_AVATARS[DEFAULT_AVATAR_ID];
    if (preset) {
      testIdSuffix = `preset-${preset.id}`;
      content = (
        <div style={{ width: px, height: px }} className="block">
          {preset.render()}
        </div>
      );
    } else {
      content = (
        <span className="text-xs font-bold text-white">{initials || '?'}</span>
      );
    }
  }

  return (
    <span
      className={`${wrapperBase} ${ring} ${className}`}
      style={{ width: px, height: px, minWidth: px, minHeight: px, maxWidth: px, maxHeight: px }}
      data-testid={testId || `user-avatar-${testIdSuffix}`}
      title={name || ''}
    >
      {content}
    </span>
  );
};

export default UserAvatar;
